import { API_URLS } from "../Constants/api.constants";
import api from "./api";
import { errorHandling } from "./error.handling";

const basePath = API_URLS.REPORTS;

const shouldAppendId = (value) => {
  if (value === undefined || value === null || value === "" || value === "TODOS") {
    return false;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) || numericValue > 0;
};

const appendFilterParams = (queryString, payload = {}) => {
  const groupId = payload.GroupId ?? payload.groupId;
  const vehicleId = payload.VehicleId ?? payload.vehicleId;
  const hasVehicleId = shouldAppendId(vehicleId);

  if (!hasVehicleId && shouldAppendId(groupId)) {
    queryString.append("GroupId", groupId);
  }

  if (hasVehicleId) {
    queryString.append("VehicleId", vehicleId);
  }
};

const normalizeSummaryVehicle = (item = {}) => ({
  licensePlate: item.LicensePlate ?? item.licensePlate,
  licensePlateType: item.LicensePlateType ?? item.licensePlateType,
  vehicleGroupName: item.VehicleGroupName ?? item.vehicleGroupName,
  color: item.Color ?? item.color,
  vehicleModel: item.VehicleModel ?? item.vehicleModel,
  manufacturer: item.Manufacturer ?? item.manufacturer,
  vehicleCategory: item.VehicleCategory ?? item.vehicleCategory,
  manufactureYear: item.ManufactureYear ?? item.manufactureYear,
  modelYear: item.ModelYear ?? item.modelYear,
});

const normalizeSummaryVehiclesResponse = (response) => {
  if (Array.isArray(response)) {
    return response.map(normalizeSummaryVehicle);
  }

  if (Array.isArray(response?.items)) {
    return {
      ...response,
      items: response.items.map(normalizeSummaryVehicle),
    };
  }

  return response;
};

const buildQuery = (payload = {}, includeSplitByPlateSheets = false) => {
  const queryString = new URLSearchParams();
  appendFilterParams(queryString, payload);

  if (includeSplitByPlateSheets) {
    queryString.append(
      "SplitByPlateSheets",
      payload.SplitByPlateSheets ?? payload.splitByPlateSheets ?? false
    );
  }

  const query = queryString.toString();
  return query ? `?${query}` : "";
};

async function fetchSummaryVehicles(payload) {
  try {
    const response = await api.get(`${basePath}/summary-vehicles${buildQuery(payload)}`);
    const treated = errorHandling.handleResponse(
      response,
      "summaryVehiclesService.fetchSummaryVehicles"
    );

    return normalizeSummaryVehiclesResponse(treated);
  } catch (error) {
    errorHandling.cathError(error, "summaryVehiclesService.fetchSummaryVehicles");
    return null;
  }
}

async function fetchSummaryVehiclesPdf(payload) {
  try {
    const response = await api.get(
      `${basePath}/summary-vehicles/pdf${buildQuery(payload, true)}`,
      { responseType: "blob" }
    );

    return response?.data ?? null;
  } catch (error) {
    errorHandling.cathError(error, "summaryVehiclesService.fetchSummaryVehiclesPdf");
    return null;
  }
}

async function fetchSummaryVehiclesXls(payload) {
  try {
    const response = await api.get(
      `${basePath}/summary-vehicles/xls${buildQuery(payload, true)}`,
      { responseType: "blob" }
    );

    return response?.data ?? null;
  } catch (error) {
    errorHandling.cathError(error, "summaryVehiclesService.fetchSummaryVehiclesXls");
    return null;
  }
}

async function fetchSummaryVehiclesReport(payload, download) {
  if (!download) {
    return fetchSummaryVehicles(payload);
  }

  const normalizedType = `${payload?.fileType || ""}`.toLowerCase();
  const downloadPayload = {
    ...payload,
    SplitByPlateSheets: payload?.SplitByPlateSheets ?? payload?.splitByPlateSheets ?? false,
  };

  if (normalizedType === "pdf") {
    return fetchSummaryVehiclesPdf(downloadPayload);
  }

  if (normalizedType === "xls" || normalizedType === "xlsx") {
    return fetchSummaryVehiclesXls(downloadPayload);
  }

  return null;
}

export const summaryVehiclesService = {
  fetchSummaryVehicles,
  fetchSummaryVehiclesPdf,
  fetchSummaryVehiclesXls,
  fetchSummaryVehiclesReport,
};
